import {
  Banner,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  DisabledWithReason,
  cn,
} from '@dawn/ui';
import { Mail, UserPlus } from 'lucide-react';
import { useState } from 'react';

import { useAdminRosterQuery } from '@/hooks/queries/use-admin-roster-query';
import { useMaxAdmins } from '@/hooks/queries/use-max-admins';
import { usePermissions } from '@/hooks/use-permissions';
import type { InvitationListItem } from '@/lib/api';
import { countUsedSlots } from '@/lib/invitation-status';

import { AdminRoleDialog } from './AdminRoleDialog';
import { AdminsTable, MANAGE_REASON } from './AdminsTable';
import { InvitationsTable } from './InvitationsTable';

/**
 * Admin roster pill: "N / max admins". Shifts to a warning tone with one slot
 * left and an error tone at the cap, so the user feels the limit approaching.
 */
function LimitIndicator({ used, max }: { used: number; max: number }) {
  const atCap = used >= max;
  const nearCap = used === max - 1;

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border px-3 py-1 text-sm font-medium tabular-nums',
        atCap
          ? 'border-error/30 bg-error/10 text-error'
          : nearCap
            ? 'border-dawn-orange/30 bg-orange-100 text-dawn-orange'
            : 'border-grey-300 bg-grey-100 text-muted-foreground'
      )}
      data-testid="admin-limit-indicator"
    >
      {used} / {max} users
    </span>
  );
}

/** Loading placeholder mirroring a table's row rhythm. */
function TableSkeleton() {
  return (
    <div className="space-y-3 py-2" data-testid="admins-loading">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="h-10 animate-pulse rounded-md bg-grey-100" />
      ))}
    </div>
  );
}

/**
 * Admin List & Management view. Two tables — admins (active or suspended) and
 * invitations — read live from GET /admins, plus an invite action and a slot
 * limit indicator. All mutations (invite / edit roles / resend / revoke / suspend / activate)
 * hit real endpoints and refetch the roster on success, following the standard
 * loading / success / error pattern (TDEV-140/141).
 */
export function AdminsSection() {
  const [inviteOpen, setInviteOpen] = useState(false);
  // Held here, not in InvitationsTable: the empty state replaces the table when the last
  // invitation goes, and the dialog must outlive it to show why the save failed (TDEV-1213 New-4).
  const [editInvitation, setEditInvitation] = useState<InvitationListItem | null>(null);
  const { data, isLoading, isError, refetch } = useAdminRosterQuery();
  const maxAdmins = useMaxAdmins();
  const { can } = usePermissions();

  const admins = data?.admins ?? [];
  const invitations = data?.invitations ?? [];
  const availableRoles = data?.availableRoles ?? [];
  const usedSlots = countUsedSlots(admins.length, invitations);
  const atCap = usedSlots >= maxAdmins;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <h2 className="text-xl font-semibold tracking-tight text-dawn-black">User Management</h2>
          <p className="text-sm text-muted-foreground">
            See who has access, manage invitations, and invite teammates — up to {maxAdmins} users.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {!isLoading && !isError && <LimitIndicator used={usedSlots} max={maxAdmins} />}
          {/* Below and end-aligned: the button sits at the top-right corner of the scrolling page. */}
          <DisabledWithReason
            reason={can('users.manage') ? null : MANAGE_REASON}
            placement="bottom-end"
          >
            <Button
              leadingIcon={<UserPlus className="h-4 w-4" />}
              onClick={() => setInviteOpen(true)}
              disabled={isLoading || isError || atCap}
              title={atCap ? `You've reached the ${maxAdmins}-user limit` : undefined}
              data-testid="invite-admin-button"
            >
              Invite user
            </Button>
          </DisabledWithReason>
        </div>
      </div>

      {isError ? (
        <Banner
          variant="error"
          className="items-start rounded-md border"
          data-testid="admins-error"
        >
          <div className="flex flex-1 flex-col items-start gap-2">
            <span>Couldn&apos;t load your user list.</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void refetch()}
              data-testid="admins-retry-button"
            >
              Retry
            </Button>
          </div>
        </Banner>
      ) : (
        <>
          <Card className="rounded-xl">
            <CardHeader>
              <CardTitle>Users</CardTitle>
              <CardDescription>
                People with access to this dashboard — active or suspended.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <TableSkeleton />
              ) : (
                <AdminsTable admins={admins} availableRoles={availableRoles} />
              )}
            </CardContent>
          </Card>

          <Card className="rounded-xl">
            <CardHeader>
              <CardTitle>Invitations</CardTitle>
              <CardDescription>
                People who&apos;ve been invited but haven&apos;t joined yet.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <TableSkeleton />
              ) : invitations.length === 0 ? (
                <div
                  className="flex flex-col items-center justify-center gap-3 py-12 text-center"
                  data-testid="invitations-empty"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-orange-100">
                    <Mail className="h-6 w-6 text-dawn-orange" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-dawn-black">No pending invitations</p>
                    <p className="text-sm text-muted-foreground">
                      Invite a teammate to add them to your roster.
                    </p>
                  </div>
                </div>
              ) : (
                <InvitationsTable
                  invitations={invitations}
                  atCap={atCap}
                  onEdit={setEditInvitation}
                />
              )}
            </CardContent>
          </Card>
        </>
      )}

      <AdminRoleDialog
        mode="invite"
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        availableRoles={availableRoles}
      />
      {editInvitation && (
        <AdminRoleDialog
          key={editInvitation.id}
          mode="edit-invitation"
          invitation={editInvitation}
          open
          onOpenChange={(open) => !open && setEditInvitation(null)}
          availableRoles={availableRoles}
        />
      )}
    </div>
  );
}
